import { Card, Alert, Row, Col, Form, Button } from "react-bootstrap";
import { VscSend } from "react-icons/vsc";
import { GrInProgress } from "react-icons/gr";
import { VscDebugLineByLine } from "react-icons/vsc";
import { FaCheck, FaRegStopCircle } from "react-icons/fa";
import { PiWarningBold } from "react-icons/pi";
import Tooltip from "../../../controls/common/tooltip";
import WidgetBase from "../common/widgetBase";
import Message from "./../common/message";
import Converter from "../../converter";
import Utils from "context/utils"
import Preferences, { withPreferences } from 'context/preferences';
import "./index.css";

class ChatNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      inputMessage: "",
      execution: null,
      progress: false,
      progressMessage: null,
      error: false,
      errorMessage: null,
    };

    this.activeStatuses = new Set(["creating", "running", "stopping"]);
    this.inactiveStatuses = new Set(["completed", "failed", "stopped"]);

    this.mounted = false;
  }

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;
    this.continueAutoRefresh();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    // Detect session<->execution mismatch and wipe local state if that happens
    // Detect session<->execution mismatch and wipe local state if that happens
    const session = this.context.session;
    if ( this.state.execution && session && this.state.execution["session_id"] !== session.id ) {
      this.setState({
        execution: null,
        progress: false, progressMessage: null, error: false, errorMessage: null
      });
    }
  }

  canSendMessage() {
    return (
      !this.state.progress
      && !( this.state.execution && this.activeStatuses.has(this.state.execution["status"]) )
    );
  }

  sendMessage() {
    // Don't proceed if there is an active execution or there is no message
    if ( !this.canSendMessage() ) {
      return;
    }

    // Enable session synchronization
    this.props.data.shellInterface.enableSessionSync(true);

    // Clear state prior to starting an execution
    this.setState({
      inputMessage: "",
      execution: null,
      progress: true, progressMessage: "Initiating workflow execution...",
      error: false, errorMessage: null
    });

    // Construct workflow input
    const userInput = Converter.getComponentPortValueData(this.props.data.component, "user_input");
    const workflowAlias = Converter.getComponentPortValueData(this.props.data.component, "send_message_workflow");
    const inputData = [
      {
        "alias": userInput,
        "value": {
          "type": "message",
          "message": {
            "role": "user",
            "content": [
              {
                "type": "text",
                "text": this.state.inputMessage
              }
            ],
            "attributes": {
              "id": Utils.generateUniqueId(),
              "created_at": Utils.currentTimeUtcIso()
            }
          }
        }
      }
    ];

    const session = this.context.session;
    if (!session) return;
    const sessionId = session.id;
    const revisionId = this.props.data.shellInterface.getRevision()["id"];
    const body = {
      "engine": "workflow_blobhub",
      "command": "create_execution",
      "session_id": sessionId,
      "alias": workflowAlias,
      "input_data": inputData
    };
    this.props.data.shellInterface.getApp().api.revisionsIdDataCommandPost(revisionId, body)
      .then( response => {
        // Parse response
        const execution = response.data["execution"];

        // Indicate success
        this.setState({
          execution: execution,
          progress: false, progressMessage: null, error: false, errorMessage: null
        });
      }, error => {
        // Indicate failure
        this.setState({
          progress: false, progressMessage: null,
          error: true, errorMessage: "Failed to start workflow execution: " + error.message
        });
      });
  }

  continueAutoRefresh() {
    if ( !this.mounted ) {
      return;
    }
    setTimeout(() => {
      // Issue refresh if execution is still in one of active statuses
      if ( this.state.execution && this.activeStatuses.has(this.state.execution["status"]) ) {
        this.refreshExecution();
      }

      // Continue timer unconditionally
      this.continueAutoRefresh();
    }, 1000);
  }

  refreshExecution() {
    const executionId = this.state.execution["id"];
    const revisionId = this.props.data.shellInterface.getRevision()["id"];
    const body = {
      "engine": "workflow_blobhub",
      "command": "get_execution",
      "execution_id": executionId
    };
    this.props.data.shellInterface.getApp().api.revisionsIdDataQueryPost(revisionId, body)
      .then( response => {
        // Parse response
        const execution = response.data["execution"];

        // Update state
        this.setState({ execution: execution });
      }, error => {
        // Stop refreshing and hide execution details
        this.setState({ execution: null });
      });
  }

  createExecutionPath() {
    const session = this.context.session;
    const sessionId = session.id;
    const executionId = this.state.execution.id;
    return this.props.data.shellInterface.createWorkflowPath("debugger", sessionId, executionId);
  }

  stopExecution() {
    // Send command to stop execution
    const executionId = this.state.execution["id"];
    const revisionId = this.props.data.shellInterface.getRevision()["id"];
    const body = {
      "engine": "workflow_blobhub",
      "command": "stop_execution",
      "execution_id": executionId
    };
    this.props.data.shellInterface.getApp().api.revisionsIdDataCommandPost(revisionId, body)
      .then( response => {
        // Refresh execution state
        this.refreshExecution();
      }, error => {
        console.error("Failed to stop execution:", error);
      });
  }

  getClassName() {
    return "playground-chat-node";
  }

  renderContent() {
    const timezone = this.props.preferences["app.timezone"];

    // Widget dimensions
    const width = Converter.getComponentPortValueData(this.props.data.component, "width");
    const height = Converter.getComponentPortValueData(this.props.data.component, "height");

    // Find history data
    const [ historyValue, historyWarning ] = this.loadObjectValue(
      this.props.data.component, "history", "messages");
    let history = historyValue ? historyValue["messages"] : null;

    if ( history ) {
      // Trim to max length
      const maxMessages = Converter.getComponentPortValueData(this.props.data.component, "max_messages");
      if ( maxMessages ) {
        history = history.slice(0, maxMessages);
      }

      // Reverse for rendering purposes
      history = [...history];
      history.reverse();
    }

    // Find out what is the default content view
    const contentView = Converter.getComponentPortValueData(this.props.data.component, "content_view");

    // Find user input and message workflow details
    const userInput = Converter.getComponentPortValueData(this.props.data.component, "user_input");
    const sendMessageWorkflow = Converter.getComponentPortValueData(this.props.data.component, "send_message_workflow");
    const workflowWarning
      = userInput && sendMessageWorkflow
      ? null : "\"user_input\" or \"send_message_workflow\" value is not specified.";

    // Retrieve session from context
    const session = this.context.session;

    // Render chat content
    return (
      <>
        <Card.Body className="nodrag nowheel" style={{
          "width": `${width}px`,
          "height": `${height}px`,
          "max-height": `${height}px`,
          "overflow": "scroll"
        }}>
          {historyWarning &&
            <Alert variant="info">
              {historyWarning}
            </Alert>
          }
          {(!historyWarning && !history) &&
            <Alert variant="warning">
              History object contains no value.
            </Alert>
          }
          {(!historyWarning && history) &&
            <>
              {history.map((messageValue, index) => (
                <Message
                  key={index}
                  value={messageValue}
                  contentView={contentView}
                  timezone={timezone}
                />
              ))}
            </>
          }
        </Card.Body>

        {this.state.progress &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              <Alert variant="primary">
                {this.state.progressMessage}
              </Alert>
            </Card.Body>
          </>
        }
        {this.state.error &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              <Alert variant="warning">
                {this.state.errorMessage}
              </Alert>
            </Card.Body>
          </>
        }
        {this.state.execution &&
          <>
            {this.activeStatuses.has(this.state.execution["status"]) &&
              <>
                <hr className="playground-chat-node-separator"/>
                <Card.Body className="nodrag nowheel">
                  <Card.Text>
                    <Row className="align-items-center">
                      <Col xs="auto" style={{ "padding-right": "0px" }}>
                        <GrInProgress />
                      </Col>
                      <Col>
                        <span>
                          Workflow is running... <br/>
                          <span className="text-muted" style={{ "text-wrap": "wrap" }}>
                            Created: {Utils.formatTimeAgo(this.state.execution["created_at"])}
                          </span>
                        </span>
                      </Col>
                      <Col xs="auto">
                        <Tooltip text="Open in Debugger">
                          <Button size="sm" variant="light">
                            <a href={this.createExecutionPath()}>
                              <VscDebugLineByLine />
                            </a>
                          </Button>
                        </Tooltip>
                        &nbsp;&nbsp;
                        <Tooltip text="Stop execution">
                          <Button size="sm" variant="light" disabled={"running" !== this.state.execution["status"]} onClick={(event) => {
                            this.stopExecution();
                          }}>
                            <FaRegStopCircle />
                          </Button>
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card.Text>
                </Card.Body>
              </>
            }
            {this.inactiveStatuses.has(this.state.execution["status"]) &&
              <>
                <hr className="playground-chat-node-separator"/>
                <Card.Body className="nodrag nowheel">
                  <Card.Text>
                    <Row className="align-items-center">
                      <Col xs="auto" style={{ "padding-right": "0px" }}>
                        {"completed" == this.state.execution["status"] &&
                          <FaCheck />
                        }
                        {"failed" == this.state.execution["status"] &&
                          <PiWarningBold />
                        }
                        {"stopped" == this.state.execution["status"] &&
                          <FaRegStopCircle />
                        }
                      </Col>
                      <Col>
                        <span>
                          Workflow execution completed. <br/>
                          <span className="text-muted" style={{ "text-wrap": "wrap" }}>
                            Status: {this.state.execution["status"]}
                          </span>
                        </span>
                      </Col>
                      <Col xs="auto">
                        <Tooltip text="Open in Debugger">
                          <Button size="sm" variant="light">
                            <a href={this.createExecutionPath()}>
                              <VscDebugLineByLine />
                            </a>
                          </Button>
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card.Text>
                </Card.Body>
              </>
            }
          </>
        }

        {workflowWarning &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              <Alert variant="info">
                {workflowWarning}
              </Alert>
            </Card.Body>
          </>
        }
        {(session && !workflowWarning && this.props.data.shellInterface.canEditRevision()) &&
          <>
            <hr className="playground-chat-node-separator"/>
            <Card.Body className="nodrag nowheel">
              <Card.Text>
                <Row className="align-items-center">
                  <Col style={{ "padding-right": "0px" }}>
                    <Form onSubmit={(event) => {
                      event.preventDefault();
                      this.sendMessage();
                    }}>
                      <Form.Control
                        type="text" size="sm"
                        placeholder="Type your message here..."
                        value={this.state.inputMessage}
                        onChange={(event) => {
                          this.setState({ inputMessage: event.target.value })
                        }}
                      />
                    </Form>
                  </Col>
                  <Col xs="auto">
                    <Tooltip text="Send a message">
                      <Button size="sm" variant="light" disabled={!this.canSendMessage()} onClick={(event) => {
                        this.sendMessage();
                      }}><VscSend/></Button>
                    </Tooltip>
                  </Col>
                </Row>
              </Card.Text>
            </Card.Body>
          </>
        }
      </>
    );
  }
}

export default withPreferences(ChatNode, ["app.timezone"]);
